import Edit from '@mui/icons-material/Edit';
import { Grid, InputLabel, Paper, Skeleton, TextField } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

import { capitalize, get } from 'lodash';
import { Duration } from 'luxon';
import { Fragment, useMemo } from 'react';
import { Link, RouteComponentProps, useParams } from 'react-router-dom';
import { useRecoilValueLoadable } from 'recoil';
import { HashRoute } from '../../common/HashRoute';
import { formatOnOffString } from '../../common/helperFunctions/formatter';
import { useStylesBase } from '../../common/styles';
import { isUrl } from '../../common/validators';
import { IBusinessUser, Role } from '../../recoil/atoms/user';
import { methodNameEnum } from '../../recoil/selectors/makePHPRequest';
import {
  IPermissions,
  permissionsSelector,
} from '../../recoil/selectors/permissionsSelector';
import {
  endpointEnum,
  makeApiRequestCached,
} from '../../services/rest/makeAPIRequest';
import { createHoursString } from '../components/CadenceSettings/Scheduler';
import {
  getDefaultActionFields,
  getDefaultActionPayload,
} from '../components/dropdowns/DefaultAction';
import { EditAgentModal } from '../components/modals/EditAgentModal';
// import { DashboardTourExtraSpotlights } from './dashboardTour/DashboardTourExtraSpotlights';
import { IBusinessModel } from '../../api/apigQuery';
import { trackEditAgent } from '../../common/userTracking';
import { POWERDIALER_MACHINE_DETECT_PRODUCT_ID } from './ManageAccount';

const useStyles = makeStyles((_theme) => ({
  agentName: {
    fontWeight: 'bold',
  },
}));

/* eslint-disable no-unused-vars */
enum EditModalHashes {
  Profile = 'profile',
  UserHours = 'user-hours',
  Manage = 'manage',
}
/* eslint-enable no-unused-vars */

const NOT_SET_LABEL = 'Not set';

interface IEditAgentSettingsProps extends RouteComponentProps {
  userId: string;
  businessId: string;
}

export function EditAgentSettings({
  userId,
  businessId,
  history,
  match,
}: IEditAgentSettingsProps) {
  if (!userId || !businessId || !history || !match) return null;

  // BEGIN DATA FETCHING CODE COPIED FROM ../components/modals/EditAgentModal
  const {
    role,
    canAccessPowerlistProduct: _canAccessPowerlistProduct,
    hasSetSuperadminPermissions,
    isBusinessEligibleToAccessCallTranscriptions,
  } = useRecoilValueLoadable<IPermissions>(permissionsSelector)
    .contents as IPermissions;

  const [
    myBusinessUsersCached,
    _myBusinessUsersLoadable,
  ] = makeApiRequestCached<IBusinessUser | undefined>(
    {
      endpoint: endpointEnum.GET_BUSINESS_USERS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_USERS,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result?.find(
        (user) => user.userid == userId
      ) ?? cached.current,
    undefined,
    [userId]
  );

  const [businessCached, businessLoadable] = makeApiRequestCached(
    {
      endpoint: `${endpointEnum.GET_BUSINESSES}/${businessId}`,
      method: 'get',
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.map((business) => ({
        ...business,
        businessHarassmentPolicies: business.businessHarassmentPolicies?.map(
          (policy) => {
            const luxonDuration = Duration.fromMillis(
              policy.durationMin * 60 * 1000
            )
              .shiftTo('days', 'hours', 'minutes', 'seconds', 'milliseconds')
              .toObject();
            return {
              ...policy,
              days: luxonDuration.days,
              hours: luxonDuration.hours,
              minutes: luxonDuration.minutes,
            };
          }
        ),
      }))?.[0] ?? cached.current
  );

  const businessHoursMemo = useMemo(
    () =>
      businessCached.current?.businessDetails?.businessHours ?? {
        1: {
          day: 'Monday',
          hours: [],
        },
        2: {
          day: 'Tuesday',
          hours: [],
        },
        3: {
          day: 'Wednesday',
          hours: [],
        },
        4: {
          day: 'Thursday',
          hours: [],
        },
        5: {
          day: 'Friday',
          hours: [],
        },
        6: {
          day: 'Saturday',
          hours: [],
        },
        7: {
          day: 'Sunday',
          hours: [],
        },
      },
    [businessLoadable]
  );

  const billingTiers = {
    'Enterprise Unlimited': {
      tier: 1,
      unlimited: 1,
      hide: true,
    },
    Enterprise: {
      tier: 1,
      unlimited: 0,
    },
    'Integrated Unlimited': {
      tier: 2,
      unlimited: 1,
      hide: true,
    },
    Integrated: {
      tier: 2,
      unlimited: 0,
    },
    'Basic Unlimited': {
      tier: 3,
      unlimited: 1,
      hide: true,
    },
    Basic: {
      tier: 3,
      unlimited: 0,
      hide: role !== Role.SUPERADMIN,
    },
    payperuse: {
      tier: 4,
      unlimited: 0,
      hide: role !== Role.SUPERADMIN,
    },
  };
  // END DATA FETCHING CODE COPIED FROM ../components/modals/EditAgentModal

  const getAgentStatsEndpoint = () =>
    endpointEnum.GET_AGENT_STATS.replace('{businessId}', businessId).replace(
      '{userId}',
      userId
    );

  const [
    _,
    agentStatsLoadable,
    _setRefetchToggleForAgentStats,
  ] = makeApiRequestCached(
    {
      method: 'get',
      stage: 'itn',
      domain: process.env.VITE_APP_CDK_DOMAIN,
      endpoint: getAgentStatsEndpoint(),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.data ?? cached.current
  );

  const agentStats = agentStatsLoadable.contents?.response?.data?.data;
  const isAgentStatsLoading = agentStatsLoadable.state === 'loading';

  const getVoicemailMessageTypeString = () => {
    const type = isUrl(myBusinessUsersCached.current?.greeting)
      ? 'mp3'
      : 'tts' ?? '';
    switch (type) {
      case 'mp3':
        return 'MP3';
      case 'tts':
        return 'Text-to-Speech';
      default:
        return NOT_SET_LABEL;
    }
  };

  const getVoicemailGreetingString = () => {
    return (
      myBusinessUsersCached.current?.greeting ??
      'Please leave a message at the tone.'
    );
  };

  const useBusinessHoursEnabled = !!get(
    myBusinessUsersCached,
    `current.useBusinessHoursEnabled`,
    true
  );
  const [_businessDataCached, businessDataLoadable] = makeApiRequestCached<
    IBusinessModel | undefined
  >(
    {
      endpoint: endpointEnum.GET_BUSINESS_DATA,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_DATA,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result?.[0] ?? cached.current
  );
  const businessData =
    businessDataLoadable?.contents?.response?.data?.result?.[0];
  const showPauseCallRecording = useMemo(
    () => businessData?.allowPauseRecording === 1,
    [businessData]
  );

  const getActionsRoutedAt = () => {
    return (
      createHoursString(
        useBusinessHoursEnabled
          ? businessCached.current?.businessDetails?.businessHours
          : myBusinessUsersCached.current?.userHours
          ? JSON.parse(myBusinessUsersCached.current?.userHours ?? 'null')
          : businessHoursMemo
      ).split(', ') ?? []
    );
  };

  //id of this object
  const { id } = useParams<{ id?: string }>();
  // console.log('Current ID: "', id, '"');

  const closedAction = myBusinessUsersCached.current?.closedAction ?? undefined;
  const closedActionPayload = getDefaultActionPayload(
    closedAction,
    true,
    undefined,
    id
  );
  const closedActionFields = getDefaultActionFields(
    closedAction,
    true,
    undefined,
    id
  );
  // console.log(closedActionFields.userOptions.length)

  const getOutsideUserHoursActionString = () => {
    switch (closedActionPayload?.type) {
      case 'ivr':
        return 'IVR';
      default:
        return capitalize(closedActionPayload?.type ?? NOT_SET_LABEL);
    }
  };

  const getOutsideUserHoursValueString = () => {
    switch (closedActionPayload?.type) {
      case 'user':
        return (
          (closedActionFields?.userDefaultObject as any)?.calleridName ??
          NOT_SET_LABEL
        );
      case 'group':
        return (
          closedActionFields?.groupDefaultObject?.groupname ?? NOT_SET_LABEL
        );
      case 'ivr':
        return closedActionFields?.ivrDefaultObject?.IVRname ?? NOT_SET_LABEL;
      case 'queue':
        return (
          closedActionFields?.queueDefaultObject?.queuename ?? NOT_SET_LABEL
        );
      default:
        return NOT_SET_LABEL;
    }
  };

  const getBillingTierString = () => {
    const billingTier =
      billingTiers[myBusinessUsersCached.current?.userTier as string]?.tier ??
      '';
    switch (billingTier) {
      case 1:
        return 'Professional';
      case 2:
        return 'Integrated';
      case 3:
        return 'Basic';
      case 4:
        return 'Free';
      default:
        return NOT_SET_LABEL;
    }
  };

  const getMinutesPlanString = () => {
    const minutesPlan =
      billingTiers[myBusinessUsersCached.current?.userTier as string]
        ?.unlimited ?? '';
    switch (minutesPlan) {
      case 0:
        return 'Per Minute';
      case 1:
        return 'Unlimited';
      default:
        return NOT_SET_LABEL;
    }
  };

  const getPowerDialerProductString = () => {
    const powerlistProduct = myBusinessUsersCached.current?.powerlistProductId
      ? myBusinessUsersCached.current?.powerlistProductId
      : 0;
    switch (powerlistProduct) {
      case 0:
        return 'None';
      case 30:
        return 'PowerDialer One';
      case 31:
        return 'PowerDialer Four';
      case 32:
        return 'PowerDialer Ten';
      case POWERDIALER_MACHINE_DETECT_PRODUCT_ID:
        return 'PowerDialer AI Human Detect';
      default:
        return NOT_SET_LABEL;
    }
  };

  const getConnectionBoostString = () => {
    return formatOnOffString(
      myBusinessUsersCached.current?.localPresenceLite === 1
    );
  };

  const getCallTranscriptionString = () => {
    return formatOnOffString(
      myBusinessUsersCached.current?.hasPdCallTranscription === 1
    );
  };

  const getVoicemailDropString = () => {
    return capitalize(
      myBusinessUsersCached.current?.vmDropDetectionType ?? 'beep'
    );
  };

  const agentSettings = {
    stats: agentStats,
    profile: {
      fullName: `${myBusinessUsersCached.current?.fname ?? ''} ${
        myBusinessUsersCached.current?.lname ?? ''
      }`,
      firstName: myBusinessUsersCached.current?.fname ?? '',
      lastName: myBusinessUsersCached.current?.lname ?? '',
      outboundCallerIDName: myBusinessUsersCached?.current?.calleridName ?? '',
      outboundCallerIDNumber: myBusinessUsersCached.current?.callerid ?? '',
      outboundSMSNumber: myBusinessUsersCached.current?.smsDID ?? '',
      email: myBusinessUsersCached.current?.email ?? '',
    },
    voicemailMessage: {
      type: getVoicemailMessageTypeString(),
      message: getVoicemailGreetingString(),
    },
    userHours: {
      actionsRoutedAt: getActionsRoutedAt(),
      useBusinessHours: formatOnOffString(useBusinessHoursEnabled),
      outsideUserHoursAction: getOutsideUserHoursActionString(),
      hasOutsideUserHoursValue:
        ['ivr', 'group', 'user', 'queue'].indexOf(closedActionPayload?.type) !==
        -1,
      outsideUserHoursValue: getOutsideUserHoursValueString(),
    },
    userProducts: {
      billingTier: getBillingTierString(),
      minutesPlan: getMinutesPlanString(),
      powerDialerProduct: getPowerDialerProductString(),
      connectionBoost: getConnectionBoostString(),
      callTranscription: getCallTranscriptionString(),
    },
    userSettings: {
      userType: capitalize(
        myBusinessUsersCached.current?.type ?? NOT_SET_LABEL
      ),
      userExtension: myBusinessUsersCached.current?.userEXT ?? NOT_SET_LABEL,
      voicemailDrop: getVoicemailDropString(),
      othersCanViewRecordings: formatOnOffString(
        myBusinessUsersCached.current?.othersCanViewRecordings === 1
      ),
      callWaiting: formatOnOffString(
        myBusinessUsersCached.current?.allowMultipleIncomingCalls === 1
      ),
      dnd: formatOnOffString(myBusinessUsersCached.current?.DND === 1),
      callCoach: formatOnOffString(
        myBusinessUsersCached.current?.canCallCoach === 1
      ),
      agentQueue: formatOnOffString(
        myBusinessUsersCached.current?.agentQueueEnabled === 1
      ),
      canBeManaged: formatOnOffString(
        myBusinessUsersCached.current?.canBeManaged === 1
      ),
      ftcBlocking: formatOnOffString(
        myBusinessUsersCached.current?.ftcBlocking === 1
      ),
      recordCalls: formatOnOffString(
        myBusinessUsersCached.current?.recordCalls === 1
      ),
      expandedNotifications: formatOnOffString(
        myBusinessUsersCached.current?.defaultExtend === 1
      ),
      errorLogging: formatOnOffString(
        myBusinessUsersCached.current?.logLevel === 'debug'
      ),
      overrideDnc: formatOnOffString(
        myBusinessUsersCached.current?.overrideDnc === 1
      ),
      setSuperadminPermissions: formatOnOffString(
        myBusinessUsersCached.current?.setSuperadminPermissions === 1
      ),
      switchUser: formatOnOffString(
        myBusinessUsersCached.current?.switchUser === 1
      ),
      canPauseRecording: formatOnOffString(
        myBusinessUsersCached.current?.canPauseRecording === 1
      ),
    },
  };

  const classes = useStylesBase();
  const classesCustom = useStyles();

  const renderEditModal = (tabIndex) => () => {
    return (
      <EditAgentModal
        id={userId}
        show
        onHide={() => {
          history.push(match.url);
        }}
        initialTabIndex={tabIndex}
      />
    );
  };

  const getEditUrl = (hash) => `${match.url}#${hash}`;

  const isCallTranscriptionsFeaturedEnabled =
    process.env.VITE_APP_FEATURE_CALL_TRANSCRIPTIONS === '1' &&
    isBusinessEligibleToAccessCallTranscriptions === true;

  return (
    <>
      {/* <DashboardTourExtraSpotlights /> */}
      {Object.values(EditModalHashes).map((hash, index) => (
        <HashRoute
          path={match.url}
          hash={`#${hash}`}
          render={renderEditModal(index)}
          key={hash}
        />
      ))}
      <Paper className={`${classes.paper} ${classes.topSection}`} elevation={0}>
        <Grid container spacing={2} className={classes.topContainer}>
          <Grid item xs={10}>
            <h1>
              Agent Settings for{' '}
              <span className={classesCustom.agentName}>
                {agentSettings.profile.fullName}
              </span>
            </h1>
          </Grid>
          <Grid item xs={2} className={classes.editButtonContainer}>
            <Link
              to={getEditUrl(EditModalHashes.Profile)}
              className={classes.editAllLink}
              id="editAll"
              onClick={() => {
                trackEditAgent();
              }}
            >
              <Edit />
              <br />
              <span>Edit All</span>
            </Link>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <span className={classes.statsLabel}>All Calls</span>
            {isAgentStatsLoading ? (
              <Skeleton
                variant="rectangular"
                className={classes.statsValueLoading}
              />
            ) : (
              <p className={classes.statsValue}>
                {agentSettings.stats?.allCalls || '0'}
              </p>
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <span className={classes.statsLabel}>Missed %</span>
            {isAgentStatsLoading ? (
              <Skeleton
                variant="rectangular"
                className={classes.statsValueLoading}
              />
            ) : (
              <p className={classes.statsValue}>
                {agentSettings.stats?.incomingMissedPercent || '0.0%'}
              </p>
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <span className={classes.statsLabel}>Outgoing Connection %</span>
            {isAgentStatsLoading ? (
              <Skeleton
                variant="rectangular"
                className={classes.statsValueLoading}
              />
            ) : (
              <p className={classes.statsValue}>
                {agentSettings.stats?.outgoingConnectionPercent || '0.0%'}
              </p>
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <span className={classes.statsLabel}>Avg. Duration</span>
            {isAgentStatsLoading ? (
              <Skeleton
                variant="rectangular"
                className={classes.statsValueLoading}
              />
            ) : (
              <p className={classes.statsValue}>
                {agentSettings.stats?.averageDuration || '0:00:00'}
              </p>
            )}
          </Grid>
        </Grid>
        <hr className={classes.topDivider} />
        <Grid
          container
          spacing={2}
          id="profileSection"
          className={classes.sectionContainer}
        >
          <Grid item xs={10}>
            <h2 className={classes.sectionTitle}>Profile</h2>
          </Grid>
          <Grid item xs={2} className={classes.editButtonContainer}>
            <Link
              to={getEditUrl(EditModalHashes.Profile)}
              className={classes.editLink}
            >
              <Edit />
            </Link>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="First Name *"
              value={agentSettings.profile.firstName}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              variant="standard"
              fullWidth
              multiline
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Last Name *"
              value={agentSettings.profile.lastName}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              variant="standard"
              fullWidth
              multiline
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Outbound Caller ID Name *"
              value={agentSettings.profile.outboundCallerIDName}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              variant="standard"
              fullWidth
              multiline
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Outbound Caller ID Number *"
              value={agentSettings.profile.outboundCallerIDNumber}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              variant="standard"
              fullWidth
              multiline
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Outbound SMS Number *"
              value={agentSettings.profile.outboundSMSNumber}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              variant="standard"
              fullWidth
              multiline
            />
          </Grid>
          <Grid item xs={12} sm={6} md={9}>
            <TextField
              label="Email *"
              value={agentSettings.profile.email}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              variant="standard"
              fullWidth
              multiline
            />
          </Grid>
        </Grid>
      </Paper>
      <Paper className={classes.paper} elevation={0}>
        <Grid
          container
          spacing={2}
          id="userHoursSection"
          className={classes.sectionContainer}
        >
          <Grid item xs={10}>
            <h2 className={classes.sectionTitle}>User Hours</h2>
          </Grid>
          <Grid item xs={2} className={classes.editButtonContainer}>
            <Link
              to={getEditUrl(EditModalHashes.UserHours)}
              className={classes.editLink}
            >
              <Edit />
            </Link>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <InputLabel shrink>Actions will be routed</InputLabel>
            {agentSettings.userHours.actionsRoutedAt.map((action) => (
              <Fragment key={action}>
                <span>{action}</span>
                <br />
              </Fragment>
            ))}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Use Business Hours"
              value={agentSettings.userHours.useBusinessHours}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              variant="standard"
              fullWidth
              multiline
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Outside User Hours Action"
              value={agentSettings.userHours.outsideUserHoursAction}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              variant="standard"
              fullWidth
              multiline
            />
          </Grid>
          {agentSettings.userHours.hasOutsideUserHoursValue && (
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label={agentSettings.userHours.outsideUserHoursAction}
                value={agentSettings.userHours.outsideUserHoursValue}
                InputProps={{
                  readOnly: true,
                  disableUnderline: true,
                }}
                variant="standard"
                fullWidth
                multiline
              />
            </Grid>
          )}
        </Grid>
      </Paper>
      <Paper className={classes.paper} elevation={0}>
        <Grid
          container
          spacing={2}
          id="userProductsSection"
          className={classes.sectionContainer}
        >
          {role !== Role.USER ? (
            <Grid item xs={10}>
              <h2 className={classes.sectionTitle}>Manage</h2>
            </Grid>
          ) : (
            <Grid item xs={10}>
              <h2 className={classes.sectionTitle}>User Settings</h2>
            </Grid>
          )}
          <Grid item xs={2} className={classes.editButtonContainer}>
            <Link
              to={getEditUrl(EditModalHashes.Manage)}
              className={classes.editLink}
            >
              <Edit />
            </Link>
          </Grid>
          {role !== Role.USER && (
            <Grid item xs={12}>
              <h3 className={classes.subsectionTitle}>User Products</h3>
            </Grid>
          )}
          {role !== Role.USER && (
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Billing Tier *"
                value={agentSettings.userProducts.billingTier}
                InputProps={{
                  readOnly: true,
                  disableUnderline: true,
                }}
                variant="standard"
                fullWidth
                multiline
              />
            </Grid>
          )}
          {role !== Role.USER && (
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Minutes Plan *"
                value={agentSettings.userProducts.minutesPlan}
                InputProps={{
                  readOnly: true,
                  disableUnderline: true,
                }}
                variant="standard"
                fullWidth
                multiline
              />
            </Grid>
          )}
          {role !== Role.USER && (
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="PowerDialer Product *"
                value={agentSettings.userProducts.powerDialerProduct}
                InputProps={{
                  readOnly: true,
                  disableUnderline: true,
                }}
                variant="standard"
                fullWidth
                multiline
              />
            </Grid>
          )}
          {role === Role.SUPERADMIN && (
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="ConnectionBoost"
                className="superStyle"
                value={agentSettings.userProducts.connectionBoost}
                InputProps={{
                  readOnly: true,
                  disableUnderline: true,
                }}
                variant="standard"
                fullWidth
                multiline
              />
            </Grid>
          )}
          {isCallTranscriptionsFeaturedEnabled &&
            (role === Role.SUPERADMIN ||
              role === Role.ADMIN ||
              role === Role.MANAGER) &&
            agentSettings && (
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Conversation Intelligence"
                  value={agentSettings.userProducts.callTranscription}
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                  }}
                  variant="standard"
                  fullWidth
                  multiline
                />
              </Grid>
            )}
          <hr className={classes.subsectionDivider} />
          {role !== Role.USER && (
            <Grid item xs={12}>
              <h3 className={classes.subsectionTitle}>User Settings</h3>
            </Grid>
          )}
          {role !== Role.USER && (
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="User Type *"
                value={agentSettings.userSettings.userType}
                InputProps={{
                  readOnly: true,
                  disableUnderline: true,
                }}
                variant="standard"
                fullWidth
                multiline
              />
            </Grid>
          )}
          {role !== Role.USER && (
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="User Extension *"
                value={agentSettings.userSettings.userExtension}
                InputProps={{
                  readOnly: true,
                  disableUnderline: true,
                }}
                variant="standard"
                fullWidth
                multiline
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Voicemail Drop *"
              value={agentSettings.userSettings.voicemailDrop}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              variant="standard"
              fullWidth
              multiline
            />
          </Grid>
          {role !== Role.USER && (
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Others Can View Recordings"
                value={agentSettings.userSettings.othersCanViewRecordings}
                InputProps={{
                  readOnly: true,
                  disableUnderline: true,
                }}
                variant="standard"
                fullWidth
                multiline
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Call Waiting"
              value={agentSettings.userSettings.callWaiting}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              variant="standard"
              fullWidth
              multiline
            />
          </Grid>
          {role !== Role.USER && (
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="DND"
                value={agentSettings.userSettings.dnd}
                InputProps={{
                  readOnly: true,
                  disableUnderline: true,
                }}
                variant="standard"
                fullWidth
                multiline
              />
            </Grid>
          )}
          {role !== Role.USER && (
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Can Call Coach"
                value={agentSettings.userSettings.callCoach}
                InputProps={{
                  readOnly: true,
                  disableUnderline: true,
                }}
                variant="standard"
                fullWidth
                multiline
              />
            </Grid>
          )}
          {role !== Role.USER && (
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Agent Queue"
                value={agentSettings.userSettings.agentQueue}
                InputProps={{
                  readOnly: true,
                  disableUnderline: true,
                }}
                variant="standard"
                fullWidth
                multiline
              />
            </Grid>
          )}
          {role !== Role.USER && (
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Can Be Call Coached"
                value={agentSettings.userSettings.canBeManaged}
                InputProps={{
                  readOnly: true,
                  disableUnderline: true,
                }}
                variant="standard"
                fullWidth
                multiline
              />
            </Grid>
          )}
          {role === Role.SUPERADMIN &&
            process.env.VITE_APP_FEATURE_FTC_BLOCKING === '1' && (
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="DNC Algorithm"
                  className="superStyle"
                  value={agentSettings.userSettings.ftcBlocking}
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                  }}
                  variant="standard"
                  fullWidth
                  multiline
                />
              </Grid>
            )}
          {role !== Role.USER && (
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Record Calls"
                value={agentSettings.userSettings.recordCalls}
                InputProps={{
                  readOnly: true,
                  disableUnderline: true,
                }}
                variant="standard"
                fullWidth
                multiline
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Expanded Notifications"
              value={agentSettings.userSettings.expandedNotifications}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              variant="standard"
              fullWidth
              multiline
            />
          </Grid>
          {role === Role.SUPERADMIN && (
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Error Logging"
                className="superStyle"
                value={agentSettings.userSettings.errorLogging}
                InputProps={{
                  readOnly: true,
                  disableUnderline: true,
                }}
                variant="standard"
                fullWidth
                multiline
              />
            </Grid>
          )}
          {process.env.VITE_APP_AGENT_OVERRIDE_DNC === '1' &&
            role !== Role.USER && (
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Override DNC"
                  value={agentSettings.userSettings.overrideDnc}
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                  }}
                  variant="standard"
                  fullWidth
                  multiline
                />
              </Grid>
            )}

          {hasSetSuperadminPermissions && (
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Set Superadmin Permissions"
                className="superStyle"
                value={agentSettings.userSettings.setSuperadminPermissions}
                InputProps={{
                  readOnly: true,
                  disableUnderline: true,
                }}
                variant="standard"
                fullWidth
                multiline
              />
            </Grid>
          )}
          {hasSetSuperadminPermissions && (
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Switch User"
                className="superStyle"
                value={agentSettings.userSettings.switchUser}
                InputProps={{
                  readOnly: true,
                  disableUnderline: true,
                }}
                variant="standard"
                fullWidth
                multiline
              />
            </Grid>
          )}
          {showPauseCallRecording && (
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Allow Pause Recording"
                value={agentSettings.userSettings.canPauseRecording}
                InputProps={{
                  readOnly: true,
                  disableUnderline: true,
                }}
                variant="standard"
                fullWidth
                multiline
              />
            </Grid>
          )}
        </Grid>
      </Paper>
    </>
  );
}
